import axios from 'axios';

/**
 * The ISO 8583 card corridor's participant API — the card engine, proxied at
 * /iso8583/api. The same session token as the other two corridors: the engine
 * verifies it with the shared SESSION_JWT_SECRET and resolves the tenant from it.
 */
const api = axios.create({
  baseURL: '/iso8583/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // A rejected session is the same event on every corridor: sign in again.
    if (error.response?.status === 401) {
      const onAuthPage = window.location.pathname.startsWith('/login');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('tenantId');
      if (!onAuthPage) window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

/** Saves a blob the browser was handed, under the server's filename. */
export function saveBlob({ blob, filename }) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const cardEngineApi = {
  /** Public: whether the card link is listening, and on what terms. */
  health: async () => (await api.get('/healthz')).data,
};

export const cardCertificationApi = {
  /** The 22 cases, the steps for each, and the test issuer's rules. */
  getSuite: async () => (await api.get('/certification/suite')).data,
  /** The open run, as a signed report of what has been exercised. */
  progress: async () => (await api.get('/certification/progress')).data,
  /** Closes the run and issues its report. */
  finalize: async () => (await api.post('/certification/finalize')).data,
  /** The last issued report, or null when none has been. */
  latestReport: async () => {
    const r = await api.get('/certification/report/latest');
    return r.status === 204 ? null : r.data;
  },
  /** One case's steps. */
  caseInstruction: async (caseId) => (await api.get(`/certification/instructions/case/${caseId}`)).data,
  /**
   * Asks the switch to start an issuer-side case on the caller's signed-on link.
   * Resolves with { started, message } — also for a 409, whose message says why
   * nothing was sent.
   */
  trigger: async (caseId) => {
    try {
      return (await api.post(`/certification/trigger/${caseId}`)).data;
    } catch (e) {
      if (e.response?.status === 409 || e.response?.status === 503) return e.response.data;
      throw e;
    }
  },
  /** The certificate PDF; 409 until the CoE has approved. */
  downloadCertificate: async () => {
    const r = await api.get('/certification/certificate', { responseType: 'blob' });
    const m = (r.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/);
    return { blob: r.data, filename: m ? m[1] : 'card-certificate.pdf' };
  },
};

/** The dialect this switch speaks: its policies, and its data element table. */
export const cardDialectApi = {
  /** The profile in force, and the list of every profile that can be read. */
  get: async () => (await api.get('/dialect')).data,
  /** One named profile, for the Dialect page's tabs. */
  getOne: async (id) => (await api.get(`/dialect/${encodeURIComponent(id)}`)).data,
};

export const cardLedgerApi = {
  /**
   * The run's messages, newest first.
   * @param {{run?: 'current'|'all', direction?: string, mti?: string, stan?: string, limit?: number}} params
   */
  list: async (params = {}) => (await api.get('/ledger', { params })).data,
  get: async (id) => (await api.get(`/ledger/${id}`)).data,
};

/**
 * The institution ids this participant's gateway signs on with (DE 32). A card
 * participant starts with one the Sandbox assigned, flagged `sandboxAssigned`.
 */
export const cardInstitutionApi = {
  mine: async () => (await api.get('/institutions')).data,
  /** Replaces them (an admin only): each 1 to 11 digits, and no other participant's. */
  replace: async (institutionIds) => (await api.put('/institutions', { institutionIds })).data,
};

/**
 * The Message Generator: building a frame, and sending it on the link the
 * engine holds for this participant. Rehearsal only — the engine does not score
 * what is sent here, and says so in every answer.
 */
export const cardConsoleApi = {
  /** The ready-made messages, with fresh trace numbers and this participant's institution id. */
  templates: async () => (await api.get('/generate/templates')).data,

  /**
   * Every data element the dialect defines for a message type, with its format —
   * what the suite requires is marked, and so is what the message already has.
   */
  fields: async (mti, present = []) =>
    (await api.get('/generate/fields', { params: { mti, present: present.join(',') } })).data,

  /** Builds one frame: `{ mti, fields: { '7': '…', '70': '001' } }`. */
  generate: async (mti, fields) => (await api.post('/generate', { mti, fields })).data,

  /**
   * Reads a frame and says what it is — nothing is sent, stored or scored. A
   * refusal (unreadable hex) comes back as data, so the page can show it.
   */
  explain: async (frameHex) => {
    try {
      return (await api.post('/explain', { frameHex })).data;
    } catch (e) {
      if (e.response?.data?.error) return { error: e.response.data.error };
      throw e;
    }
  },

  /** Whether a link is open, what it signed on as, and anything the switch sent unprompted. */
  state: async () => (await api.get('/console')).data,

  /** Opens the link (replacing any this participant already had). */
  connect: async () => (await api.post('/console/connect', {})).data,

  /** Closes it. Leaving the page does the same, eventually: idle links are reaped. */
  disconnect: async () => (await api.post('/console/disconnect', {})).data,

  /**
   * Writes one frame and returns what came back. A refusal — a prefix that
   * disagrees with the message, no link open — is data, not an exception: the
   * page shows what the engine said rather than "request failed".
   */
  send: async (frameHex) => {
    try {
      return (await api.post('/console/send', { frameHex })).data;
    } catch (e) {
      if (e.response?.data?.error) return { error: e.response.data.error };
      throw e;
    }
  },
};
